import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  Compass, Waves, BookOpen, PenLine, MessageCircle, Radio, GraduationCap,
  Library, Sparkles, User, Shield, LogOut, Menu, X, Flame, CalendarPlus, LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { COMPASS_IMG } from "@/lib/api";
import CheckinReminder from "@/components/CheckinReminder";
import NotificationBell from "@/components/NotificationBell";

const items = [
  { to: "/community", label: "Community", icon: Waves, testId: "nav-community" },
  { to: "/today", label: "Today", icon: Compass, testId: "nav-today" },
  { to: "/journal", label: "Journal", icon: PenLine, testId: "nav-journal" },
  { to: "/meetings", label: "Meetings", icon: Radio, testId: "nav-meetings" },
  { to: "/events", label: "Events", icon: CalendarPlus, testId: "nav-events" },
  { to: "/programs", label: "Programs", icon: GraduationCap, testId: "nav-programs" },
  { to: "/resources", label: "Resources", icon: LifeBuoy, testId: "nav-resources" },
  { to: "/library", label: "Library", icon: Library, testId: "nav-library" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testId: "nav-messages" },
  { to: "/ask-ai", label: "Ask AI", icon: Sparkles, testId: "nav-ask-ai" },
  { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
];

const Brand = () => (
  <div className="flex items-center gap-2.5 px-1">
    <img src={COMPASS_IMG} alt="The Daily Compass" className="w-9 h-9 rounded-full object-cover shadow-lg shadow-primary/30 compass-glow" />
    <div className="leading-tight">
      <p className="font-heading text-base font-semibold text-foreground">The Daily</p>
      <p className="font-heading text-base font-semibold -mt-1 text-primary">Compass</p>
    </div>
  </div>
);

const Links = ({ onNavigate, isAdmin }) => (
  <nav className="flex flex-col gap-0.5">
    {items.map(({ to, label, icon: Icon, testId }) => (
      <NavLink key={to} to={to} data-testid={testId} onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`
        }>
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
        {label}
      </NavLink>
    ))}
    {isAdmin && (
      <NavLink to="/admin" data-testid="nav-admin"
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? "bg-brass/20 text-brass" : "text-brass/80 hover:bg-secondary"
          }`
        }>
        <Shield className="w-[18px] h-[18px]" /> Admin Hub
      </NavLink>
    )}
  </nav>
);

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/auth"); };

  return (
    <div className="min-h-screen flex bg-background sea-gradient">
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card/60 backdrop-blur shrink-0 fixed h-screen">
        <div className="h-16 flex items-center border-b border-border px-4"><Brand /></div>
        <div className="flex-1 overflow-y-auto p-3"><Links isAdmin={isAdmin} /></div>
        <div className="p-3 border-t border-border">
          <button data-testid="logout-button" onClick={doLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-destructive transition-colors">
            <LogOut className="w-[18px] h-[18px]" /> Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-nightsea/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-card flex flex-col animate-fade-up">
            <div className="h-16 flex items-center justify-between border-b border-border px-4">
              <Brand />
              <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3"><Links isAdmin={isAdmin} onNavigate={() => setOpen(false)} /></div>
            <div className="p-3 border-t border-border">
              <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive">
                <LogOut className="w-[18px] h-[18px]" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/70 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpen(true)} data-testid="open-mobile-nav">
              <Menu className="w-6 h-6 text-foreground" />
            </button>
            <div className="md:hidden"><Brand /></div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div data-testid="streak-badge" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-warning/10 text-warning">
              <Flame className="w-4 h-4" />
              <span className="text-sm font-semibold">{user?.streak ?? 0}</span>
              <span className="text-xs hidden sm:inline">day streak</span>
            </div>
            <div className="flex items-center gap-2">
              <Avatar name={user?.name || "Me"} src={user?.avatar_base64} size={36} testId="topbar-avatar" />
              <span className="text-sm font-medium hidden sm:block" data-testid="topbar-username">
                {user?.display_alias || user?.name}
              </span>
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0"><CheckinReminder /><Outlet /></main>
      </div>
    </div>
  );
};

export default Layout;
